import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Slider,
  Snackbar,
  Alert,
  TextField,
  Typography,
  LinearProgress,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import * as c from '../../../constants';
import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";

/* ------------------------------------------------------------------ */
/* COMPONENT                                                          */
/* ------------------------------------------------------------------ */

const BiomaStart_en = () => {

  const { state } = useAuth();
  const { t } = useTranslation();

  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const questionRefs = useRef({});

  /* ------------------------------------------------------------------ */
  /* FETCH                                                              */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    axios
      .get(`${c.API_URL}/api/questionnaires/biomastart_en/`)
      .then((res) => {
        setQuestionnaire(res.data);
        setLoading(false);
      });
  }, []);

  /* ------------------------------------------------------------------ */
  /* NORMALIZE BACKEND SHAPE (CRITICAL)                                  */
  /* ------------------------------------------------------------------ */

  const questions = useMemo(() => {
    if (!questionnaire) return [];

    return questionnaire.questions.map((qq) => ({
      id: qq.question.id,                     // Question.id (for submit)
      key: qq.id,                             // QuestionnaireQuestion.id (for React)
      code: qq.question.code,
      text: qq.question.text,
      question_type: qq.question.question_type,
      options: qq.question.options || [],
      required: qq.required,
      order: qq.order,
      depends_on_code: qq.depends_on_code,
      depends_on_value: qq.depends_on_value,
      prefilled_value: qq.question.prefilled_value || null,
    }));
  }, [questionnaire]);

 /* ------------------------------------------------------------------ */
  /* PREFILL ANSWERS FROM DATABASE (birthdate etc.)                                */
  /* ------------------------------------------------------------------ */
const mappedPrefill = (val) => {
  if (!val) return "";
  if (val === "M") return "male";
  if (val === "F") return "female";
  return val;
};

useEffect(() => {
  if (!questionnaire) return;

  const initialAnswers = {};

  questionnaire.questions.forEach((qq) => {
    const code = qq.question.code;
    const mapped = mappedPrefill(qq.question.prefilled_value);

    if (mapped !== "") {
      initialAnswers[code] = mapped; // ✅ normalize here
    }
  });

  setAnswers(initialAnswers);
}, [questionnaire]);


  const normalizeValue = (val) => {
  if (val === "M") return "male";
  if (val === "F") return "female";
  return val;
};


  /* ------------------------------------------------------------------ */
  /* ANSWERS                                                            */
  /* ------------------------------------------------------------------ */

  const handleChange = (code, value) => {
    setAnswers((prev) => ({ ...prev, [code]: value }));
  };

  /* ------------------------------------------------------------------ */
  /* CONDITIONAL VISIBILITY (NESTED SAFE)                                */
  /* ------------------------------------------------------------------ */

  const isQuestionVisible = (q) => {
  if (!q.depends_on_code) return true;

  const parentValue = answers[q.depends_on_code];
  if (!parentValue) return false;

  // normalize parentValue to array
  const parentValues = Array.isArray(parentValue)
    ? parentValue
    : [parentValue];

  // normalize dependent value(s) to array
  const dependsValues = Array.isArray(q.depends_on_value)
    ? q.depends_on_value
    : [q.depends_on_value];

  // check if any parent value matches any dependent value
  return parentValues.some((val) => dependsValues.includes(val));
};



  const visibleQuestions = questions.filter(isQuestionVisible);

  /* ------------------------------------------------------------------ */
  /* PROGRESS                                                           */
  /* ------------------------------------------------------------------ */

  const isAnswered = (q) => {
    if (!q.required) return true;
    const value = answers[q.code];
    if (q.question_type === "multi") {
      return Array.isArray(value) && value.length > 0;
    }
    return value !== undefined && value !== null && value !== "";
  };

  const answeredCount = visibleQuestions.filter(isAnswered).length;
  const progress =
    visibleQuestions.length > 0
      ? Math.round((answeredCount / visibleQuestions.length) * 100)
      : 0;

  /* ------------------------------------------------------------------ */
  /* SCROLL                                                             */
  /* ------------------------------------------------------------------ */

  const scrollToFirstUnanswered = () => {
    const first = visibleQuestions.find((q) => !isAnswered(q));
    if (first && questionRefs.current[first.code]) {
      questionRefs.current[first.code].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  /* ------------------------------------------------------------------ */
  /* SUBMIT                                                             */
  /* ------------------------------------------------------------------ */

  const handleSubmit = async () => {
    if (answeredCount !== visibleQuestions.length) {
      scrollToFirstUnanswered();
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      await axios.post(`${c.API_URL}/api/questionnaire-micro/submit/`, {
        questionnaire: questionnaire.id,
        answers: visibleQuestions
          .map((q) => {
            let value = answers[q.code] ?? null;
            if (q.question_type === "boolean" && value !== null) {
              value = value === "true";
            }
            if (q.question_type === "scale" && value === null) {
              value = 1;
            }
            return { question: q.id, value };
          })
          .filter((a) => a.value !== null),
      });

      setShowSuccess(true);

      await axios.patch(`${c.API_URL}/api/onboarding/status/`, {
        onboarding_status: 5,
      });
      setTimeout(() => {
        navigate("/LabvisitStep", {
          state: {
            from: location.pathname,
            message: t("onb_quest_submit_success"),
          },
        });
      }, 1500);
    } catch (err) {
      const errData = err.response?.data;
      setSubmitError(
        errData?.detail || errData?.message || JSON.stringify(errData) || t("val_server_error")
      );
    } finally {
      setSubmitting(false);
    }
  };

// ----------------------------------------
  // Advance onboarding status
  // ----------------------------------------
  const advanceOnboarding = async (stage) => {
    try {
      await axios.patch(`${c.API_URL}/api/onboarding/status/`, {
        onboarding_status: stage,
      });
    } catch (err) {
      console.error("Failed to advance onboarding:", err.response?.data || err);
    }
  };

  /* ------------------------------------------------------------------ */
  /* RENDER QUESTION                                                     */
  /* ------------------------------------------------------------------ */

  const renderQuestion = (q) => (
    <Box
      key={q.key}
      ref={(el) => (questionRefs.current[q.code] = el)}
      sx={{ mb: 4 }}
    >
      <FormControl fullWidth>
        <FormLabel>
          {q.text} {q.required && "*"}
        </FormLabel>

        {/* TEXT */}
        {q.question_type === "text" && (
          <TextField
            multiline
            required={q.required}
            value={answers[q.code] || ""}
            onChange={(e) => handleChange(q.code, e.target.value)}
            sx={{ mt: 2, mb: 3 }}
          />
        )}

        {/* NUMBER */}
        {q.question_type === "number" && (
          <TextField
            type="number"
            value={answers[q.code] || ""}
            onChange={(e) => handleChange(q.code, Number(e.target.value))}
          />
        )}

        {/* DATE */}
        {q.question_type === "date" && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={
                answers[q.code]
                  ? dayjs(answers[q.code], "YYYY-MM-DD", true)
                  : null
              }
              onChange={(val) =>
                handleChange(
                  q.code,
                  val ? val.format("YYYY-MM-DD") : ""
                )
              }
              slotProps={{
                  textField: { 
                    fullWidth: true, 
                    sx: { mt:2, mb: 3 },
                    format: "YYYY-MM-DD", // <-- forces displayed format
                  },
                }}
            />
          </LocalizationProvider>
        )}

        {/* SINGLE */}
        {q.question_type === "single" && (
          <RadioGroup
            value={answers[q.code] ? mappedPrefill(answers[q.code]) : mappedPrefill(q.prefilled_value)}
            onChange={(e) => handleChange(q.code, e.target.value)}
          >
            {q.options.map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                control={<Radio />}
                label={opt.label}
              />
            ))}
          </RadioGroup>
        )}

        {/* MULTI */}
        {q.question_type === "multi" &&
          q.options.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  checked={(answers[q.code] || []).includes(opt.value)}
                  onChange={(e) => {
                    const current = answers[q.code] || [];
                    handleChange(
                      q.code,
                      e.target.checked
                        ? [...current, opt.value]
                        : current.filter((v) => v !== opt.value)
                    );
                  }}
                />
              }
              label={opt.label}
            />
          ))}

        {/* BOOLEAN */}
        {q.question_type === "boolean" && (
          <RadioGroup
            value={answers[q.code] || ""}
            onChange={(e) => handleChange(q.code, e.target.value.toLowerCase())}
          >
            {q.options.map((opt) => (
              <FormControlLabel
                key={opt.id}
                value={String(opt.value).toLowerCase()} // normalize backend "True"/"False" -> "true"/"false"
                control={<Radio />}
                label={opt.label}
              />
            ))}

            {!q.required && (
              <FormControlLabel value="" control={<Radio />} label="No answer" />
            )}
          </RadioGroup>
        )}


        {/* SCALE */}
        {q.question_type === "scale" && (
          <Slider
            min={1}
            max={10}
            step={1}
            marks
            value={answers[q.code] ?? 1}
            onChange={(_, value) => handleChange(q.code, value)}
            valueLabelDisplay="auto"
            sx={{
              mt: 3,
              "& .MuiSlider-valueLabel": {
                fontSize: "1.2rem",   // ⬅️ larger floating label
                fontWeight: 600,
              },
              "& .MuiSlider-markLabel": {
                fontSize: "1rem",     // ⬅️ larger 1–10 labels
              },
            }}
          />
        )}

      </FormControl>
    </Box>
  );

  /* ------------------------------------------------------------------ */

  if (loading) return <CircularProgress />;

 
  return (
   
    <Box
      sx={{
        maxWidth: 600,
        mx: "auto",
        p: 4,
        bgcolor: "background.paper",
        borderRadius: 4,
        boxShadow: 2,
      }}
    >
      <Typography variant="h6" textAlign="center" gutterBottom sx={{ mb: 4 }}>
        Short Questionnaire
      </Typography>

      <LinearProgress variant="determinate" value={progress} sx={{ mb: 1, bgcolor: 'tertiary.main' }} />
      <Typography variant="subtitle2" align="center" fontWeight={400} sx={{mb:4}}>
          {answeredCount} / {visibleQuestions.length} answered
        </Typography>

      {visibleQuestions.map(renderQuestion)}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <Button
        fullWidth
        size="large"
        variant="contained"
        onClick={handleSubmit}
        disabled={submitting}
         sx={{my:2, borderRadius: 4}}
      >
        {submitting ? t("onb_quest_submitting") : t("onb_quest_submit")}
      </Button>

      <Snackbar open={showSuccess} autoHideDuration={1500} anchorOrigin={{ vertical: "top", horizontal: "center" }} sx={{top: "50%",transform: "translateY(-50%)",}}>
        <Alert severity="success">
          <Typography variant="h6">
             {t("onb_quest_submit_success")}
          </Typography>

        </Alert>
      </Snackbar>
    
    
    </Box>
  );
};

export default BiomaStart_en;
